import React, { useMemo, useState } from "react";
import "../../../css/calculators/MortgageBorrow.css";
import "../../../css/calculators/StampDutyCalculator.css";
import SecondaryButton from "../../designSystem/FilledButton";
import PrimaryButton from "../../designSystem/OutlinedButton";
import CustomSelect from "../../designSystem/CustomSelect";
import FormattedNumberInput from "./HelperFunctions/FormattedNumberInput";

const initialState = {
  purchasePrice: "",
  location: "",
  propertyType: "",
  firstTimeBuyer: false,
};

const locationOptions = [
  { value: "", label: "Please select" },
  { value: "england_ni", label: "England or Northern Ireland" },
  { value: "scotland", label: "Scotland" },
  { value: "wales", label: "Wales" },
];

const propertyTypeOptions = [
  { value: "", label: "Please select" },
  { value: "single", label: "Single property" },
  { value: "additional", label: "Additional property" },
];

const formatCurrency = (value, decimals = 0) => {
  return Number(value || 0).toLocaleString("en-GB", {
    style: "currency",
    currency: "GBP",
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  });
};

const formatPercent = (value, decimals = 0) => {
  return `${Number(value || 0).toLocaleString("en-GB", {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  })}%`;
};

const getTaxBreakdown = (price, bands) => {
  const rows = [];
  let totalTax = 0;

  for (let i = 0; i < bands.length; i++) {
    const currentBand = bands[i];
    const nextThreshold =
      i < bands.length - 1 ? bands[i + 1].threshold : Infinity;

    const taxableSum =
      price > currentBand.threshold
        ? Math.max(0, Math.min(price, nextThreshold) - currentBand.threshold)
        : 0;

    const tax = taxableSum * currentBand.rate;
    totalTax += tax;

    let label = "";

    if (nextThreshold === Infinity) {
      label = `Over ${formatCurrency(currentBand.threshold, 0)}`;
    } else if (currentBand.threshold === 0) {
      label = `Less than ${formatCurrency(nextThreshold, 0)}`;
    } else {
      label = `${formatCurrency(currentBand.threshold, 0)} to ${formatCurrency(
        nextThreshold,
        0,
      )}`;
    }

    rows.push({
      band: label,
      rate: currentBand.rate * 100,
      taxableSum,
      tax,
    });
  }

  return {
    rows,
    totalTax,
  };
};

const getEnglandNIResult = ({ price, propertyType, firstTimeBuyer }) => {
  const isAdditional = propertyType === "additional";

  const standardBands = [
    { threshold: 0, rate: 0 },
    { threshold: 125000, rate: 0.02 },
    { threshold: 250000, rate: 0.05 },
    { threshold: 925000, rate: 0.1 },
    { threshold: 1500000, rate: 0.12 },
  ];

  const additionalBands = [
    { threshold: 0, rate: 0.05 },
    { threshold: 125000, rate: 0.07 },
    { threshold: 250000, rate: 0.1 },
    { threshold: 925000, rate: 0.15 },
    { threshold: 1500000, rate: 0.17 },
  ];

  const ftbBands = [
    { threshold: 0, rate: 0 },
    { threshold: 300000, rate: 0.05 },
  ];

  const selectedBands = isAdditional
    ? additionalBands
    : firstTimeBuyer && price <= 500000
      ? ftbBands
      : standardBands;

  const { rows, totalTax } = getTaxBreakdown(price, selectedBands);

  return {
    tax: totalTax,
    breakdown: rows,
  };
};

const getScotlandResult = ({ price, propertyType, firstTimeBuyer }) => {
  const baseBands = [
    { threshold: 0, rate: 0 },
    { threshold: 145000, rate: 0.02 },
    { threshold: 250000, rate: 0.05 },
    { threshold: 325000, rate: 0.1 },
    { threshold: 750000, rate: 0.12 },
  ];

  const { rows, totalTax: baseTax } = getTaxBreakdown(price, baseBands);

  let ftbRelief = 0;
  if (firstTimeBuyer && propertyType !== "additional" && price > 145000) {
    ftbRelief = Math.min((Math.min(price, 175000) - 145000) * 0.02, 600);
  }

  const ads =
    propertyType === "additional" && price >= 40000 ? price * 0.08 : 0;

  const breakdown = [...rows];

  if (ftbRelief > 0) {
    breakdown.push({
      band: "First time buyer relief",
      rate: 0,
      taxableSum: 0,
      tax: -ftbRelief,
    });
  }

  if (ads > 0) {
    breakdown.push({
      band: "Additional dwelling supplement",
      rate: 8,
      taxableSum: price,
      tax: ads,
    });
  }

  return {
    tax: Math.max(baseTax - ftbRelief, 0) + ads,
    breakdown,
  };
};

const getWalesResult = ({ price, propertyType }) => {
  const isAdditional = propertyType === "additional";

  const mainBands = [
    { threshold: 0, rate: 0 },
    { threshold: 225000, rate: 0.06 },
    { threshold: 400000, rate: 0.075 },
    { threshold: 750000, rate: 0.1 },
    { threshold: 1500000, rate: 0.12 },
  ];

  const higherBands = [
    { threshold: 0, rate: 0.05 },
    { threshold: 180000, rate: 0.085 },
    { threshold: 250000, rate: 0.1 },
    { threshold: 400000, rate: 0.125 },
    { threshold: 750000, rate: 0.15 },
    { threshold: 1500000, rate: 0.17 },
  ];

  const { rows, totalTax } = getTaxBreakdown(
    price,
    isAdditional ? higherBands : mainBands,
  );

  return {
    tax: totalTax,
    breakdown: rows,
  };
};

const getLocationLabel = (location) => {
  return locationOptions.find((item) => item.value === location)?.label || "";
};

const getPropertyTypeLabel = (propertyType) => {
  return propertyType === "additional"
    ? "additional property"
    : "single property";
};

const getStampDutyResult = ({
  purchasePrice,
  location,
  propertyType,
  firstTimeBuyer,
}) => {
  const price = Number(purchasePrice || 0);

  if (!price || !location || !propertyType) {
    return {
      tax: 0,
      effectiveRate: 0,
      valid: false,
      breakdown: [],
      summaryText: "",
    };
  }

  let result = {
    tax: 0,
    breakdown: [],
  };

  switch (location) {
    case "england_ni":
      result = getEnglandNIResult({ price, propertyType, firstTimeBuyer });
      break;
    case "scotland":
      result = getScotlandResult({ price, propertyType, firstTimeBuyer });
      break;
    case "wales":
      result = getWalesResult({ price, propertyType });
      break;
    default:
      result = { tax: 0, breakdown: [] };
  }

  return {
    ...result,
    effectiveRate: price > 0 ? (result.tax / price) * 100 : 0,
    valid: true,
    summaryText: `On a ${getPropertyTypeLabel(
      propertyType,
    )} in ${getLocationLabel(location)} with a value of ${formatCurrency(
      price,
    )} your stamp duty will be:`,
  };
};

export default function StampDutyCalculator() {
  const [formData, setFormData] = useState(initialState);
  const [hasCalculated, setHasCalculated] = useState(false);

  const result = useMemo(() => getStampDutyResult(formData), [formData]);

const handleChange = (e) => {
  const { name, value, type, checked } = e.target;

  setFormData((prev) => ({
    ...prev,
    [name]: type === "checkbox" ? checked : value,
  }));
};

  const handleCalculate = () => {
    setHasCalculated(true);
  };

  const handleClear = () => {
    setFormData(initialState);
    setHasCalculated(false);
  };

  const disableFirstTimeBuyer =
    formData.propertyType === "additional" || formData.location === "wales";

  return (
    <div className="borrow-calculator-page">
      <div className="borrow-form-wrap">
        <div className="title burgandy">About the property</div>

        <div className="borrow-field-group">
          <div className="borrow-label content burgandy align-left">Purchase price of property(£)</div>
          <FormattedNumberInput
            name="purchasePrice"
            value={formData.purchasePrice}
            onChange={(raw) =>
              setFormData((prev) => ({
                ...prev,
                purchasePrice: raw,
              }))
            }
            className="borrow-input content"
          />
        </div>

        <div className="borrow-field-group">
          <div className="borrow-label content burgandy align-left">Choose your location</div>
          <div className="select-wrapper">
            {/* <select
              name="location"
              value={formData.location}
              onChange={handleChange}
              className="borrow-input content"
            >
              {locationOptions.map((option) => (
                <option key={option.value || "blank"} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select> */}
            <CustomSelect
              name="location"
              options={locationOptions}
              value={formData.location}
              onChange={handleChange}
              placeholder="Please select a location"
            />
          </div>
        </div>

        <div className="borrow-field-group">
          <div className="borrow-label content burgandy align-left">Property type</div>
          <div className="select-wrapper">
            {/* <select
              name="propertyType"
              value={formData.propertyType}
              onChange={handleChange}
              className="borrow-input content"
            >
              {propertyTypeOptions.map((option) => (
                <option key={option.value || "blank"} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select> */}
            <CustomSelect
              name="propertyType"
              options={propertyTypeOptions}
              value={formData.propertyType}
              onChange={handleChange}
              placeholder="Please select a property type"
            />
          </div>
        </div>

        <label
          className={`borrow-checkbox-row ${
            disableFirstTimeBuyer ? "borrow-checkbox-row-disabled" : ""
          }`}
        >
          <input
            type="checkbox"
            name="firstTimeBuyer"
            checked={disableFirstTimeBuyer ? false : formData.firstTimeBuyer}
            onChange={handleChange}
            disabled={disableFirstTimeBuyer}
            className="borrow-checkbox"
          />
          <span className="borrow-checkbox-label">I am a first time buyer</span>
        </label>

        <div
          style={{
            display: "flex",
            gap: 16,
            marginTop: 24,
            justifyContent: "flex-end",
            flexWrap: "wrap",
          }}
        >
          <SecondaryButton onClick={handleClear} text="Clear" />
          <PrimaryButton onClick={handleCalculate} text="Calculate" />
        </div>
      </div>

      {hasCalculated && result.valid && (
        <div className="borrow-result-wrap">
          <div className="result-container">
            <div className="title">Result</div>

            <p className="result-copy stamp-duty-summary-copy">
              {result.summaryText}
            </p>

            <div className="result-value">{formatCurrency(result.tax)}</div>

            <div className="result-heading stamp-duty-effective-rate">
              Effective Rate{" "}
              <strong>{formatPercent(result.effectiveRate, 0)}</strong>
            </div>

            <div className="stamp-duty-breakdown-table-wrap">
              <table className="stamp-duty-breakdown-table">
                <thead>
                  <tr>
                    <th>Tax Band</th>
                    <th>%</th>
                    <th>Taxable Sum</th>
                    <th>Tax</th>
                  </tr>
                </thead>
                <tbody>
                  {result.breakdown.map((row, index) => (
                    <tr key={`${row.band}-${index}`}>
                      <td>{row.band}</td>
                      <td>
                        {formatPercent(row.rate, row.rate % 1 !== 0 ? 1 : 0)}
                      </td>
                      <td>{formatCurrency(row.taxableSum)}</td>
                      <td>
                        {row.tax < 0
                          ? `-${formatCurrency(Math.abs(row.tax))}`
                          : formatCurrency(row.tax)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div className="result-note">
              <div className="content-semibold">PLEASE NOTE:</div>
              <p className="content">
                This stamp duty calculator is an interactive tool which should
                be used for your guidance only, and must not form part of your
                financial decision making process.
              </p>
              <p className="content">
                Your home may be repossessed if you do not keep up repayments on
                a mortgage or any other debt secured on it.
              </p>
              <p className="content">
                A mortgage is a loan secured against your home or property.
              </p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
